import { useMemo } from "react";
import type { EditProps, RaRecord } from "react-admin";
import { Edit, SimpleForm, SelectInput, FormDataConsumer, useRecordContext } from "react-admin";
import ChulaSabilEditFields from "./ChulaSabilFields";
import EstablishmentEditFields from "./EstablishmentFields";
import { SabilHouseholdMembersInput } from "../components/SabilHouseholdMembersInput";
import { isPersonalSabilType } from "@/constants";
import {
  isMuttavatteenSabilType,
  prepareMuttavatteenEditFormRecord,
  validateMuttavatteenHolderWhatsapp,
} from "../sabil-household-members";
import { transformSabilDataEdit } from "./transform-sabil-data-edit";
import { SabilAuditRemarkInput } from "../components/SabilAuditRemarkInput";
import { validateSabilEditAuditRemark } from "../requires-sabil-edit-audit-remark";

const validateSabilDataEdit = (
  values: Record<string, unknown>,
  previousData?: Record<string, unknown>
) => {
  const errors: Record<string, string> = {};
  const whatsappError = validateMuttavatteenHolderWhatsapp(values, previousData);
  if (whatsappError) {
    errors.whatsappNo = whatsappError;
  }
  const auditError = validateSabilEditAuditRemark(values, previousData);
  if (auditError) {
    errors.auditRemark = auditError;
  }
  return errors;
};

function SabilDataEditForm() {
  const record = useRecordContext<RaRecord>();
  const formRecord = useMemo(
    () => prepareMuttavatteenEditFormRecord(record as Record<string, unknown>) as RaRecord,
    [record]
  );

  return (
    <SimpleForm
      record={formRecord}
      warnWhenUnsavedChanges
      validate={validateSabilDataEdit}
      sx={{ maxWidth: 900 }}
    >
      <SelectInput
        source="sabilType"
        label="Sabil Type"
        helperText="Select any one of Chula, Establishment, Professional, Muttavatteen, or Muttavatteen Yearly"
        choices={[
          { id: "CHULA", name: "CHULA" },
          { id: "ESTABLISHMENT", name: "ESTABLISHMENT" },
          { id: "PROFESSIONAL", name: "PROFESSIONAL" },
          { id: "MUTTAVATTEEN", name: "MUTTAVATTEEN" },
          { id: "MUTTAVATTEEN_YEARLY", name: "MUTTAVATTEEN YEARLY" },
        ]}
        disabled
        fullWidth
        isRequired
      />
      <FormDataConsumer>
        {({ formData }) => {
          if (isPersonalSabilType(formData.sabilType as string)) {
            return (
              <>
                <ChulaSabilEditFields />
                {isMuttavatteenSabilType(formData.sabilType as string) ? (
                  <SabilHouseholdMembersInput />
                ) : null}
              </>
            );
          }
          if (formData.sabilType === "ESTABLISHMENT" || formData.sabilType === "PROFESSIONAL") {
            return <EstablishmentEditFields />;
          }
          return null;
        }}
      </FormDataConsumer>
      <SabilAuditRemarkInput />
    </SimpleForm>
  );
}

const SabilDataEdit = (props: EditProps) => (
  <Edit {...props} transform={transformSabilDataEdit}>
    <SabilDataEditForm />
  </Edit>
);

export default SabilDataEdit;
